/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AiManagementState } from 'store/modules/ai_management_store/types';
import { BiddingTaskComment, BiddingTaskTypes } from 'store/modules/bidding_store/types';
import {
  addTaskComment,
  createBiddingTask,
  createBiddingTasksBatch,
  listRecommendedTasks,
  CreateTaskPayload,
  RecommendedTask,
} from 'services/actions/ai_management_tasks';
import AiManagementDatetimePicker from 'components/atoms/ai_management_datetime_picker';
import CommentInput from 'components/molecules/comment_input';
import AiManagementChecklist from 'components/molecules/ai_management_checklist';
import AiManagementTaskSuggestions, {
  SuggestionDraft,
} from 'components/molecules/ai_management_task_suggestions';
import { Container } from './styles';

const TITLE_MAX = 150;
const CHECKLIST_ITEM_MAX = 255;

const toIsoDeadline = (value: string) => (value.length === 16 ? `${value}:00` : value);

interface TaskNote {
  key: number;
  text: string;
}

const addNotesInOrder = (taskId: number, notes: TaskNote[]) =>
  notes.reduce<Promise<BiddingTaskComment[]>>(
    (previous, note) =>
      previous.then((comments) =>
        addTaskComment(taskId, note.text)
          .then((res) => (res?.data?.comment?.id ? [...comments, res.data.comment] : comments))
          .catch(() => comments),
      ),
    Promise.resolve([]),
  );

interface AiManagementTaskNewProps {
  biddingId: number;
  onClose: () => void;
  onCreated: (task: BiddingTaskTypes) => void;
  onCreatedMany: (tasks: BiddingTaskTypes[]) => void;
}

const AiManagementTaskNew: React.FC<AiManagementTaskNewProps> = ({
  biddingId,
  onClose,
  onCreated,
  onCreatedMany,
}) => {
  const team = useSelector<ApplicationState, AiManagementState['team']>(
    (state) => state.session.ai_management.team,
  );

  const [suggestions, setSuggestions] = useState<RecommendedTask[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);

  const [step, setStep] = useState<'choice' | 'form' | 'suggestions'>('choice');
  const [title, setTitle] = useState('');
  const [responsibleId, setResponsibleId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [description, setDescription] = useState('');
  const [checklist, setChecklist] = useState<string[]>([]);
  const [notes, setNotes] = useState<TaskNote[]>([]);
  const noteKey = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoadingSuggestions(true);
    listRecommendedTasks(biddingId)
      .then((res) => {
        if (!res || res.status >= 400) return;
        setSuggestions(Array.isArray(res.data?.tarefas) ? res.data.tarefas : []);
      })
      .finally(() => setLoadingSuggestions(false));
  }, [biddingId]);

  const addChecklistItem = (content: string) => {
    const trimmed = content.trim().slice(0, CHECKLIST_ITEM_MAX);
    if (trimmed) setChecklist((items) => [...items, trimmed]);
  };

  const removeChecklistItem = (index: number) =>
    setChecklist((items) => items.filter((_, position) => position !== index));

  const addNote = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    noteKey.current += 1;
    setNotes((items) => [...items, { key: noteKey.current, text: trimmed }]);
  };

  const removeNote = (key: number) => setNotes((items) => items.filter((note) => note.key !== key));

  const suggestionPayload = (draft: SuggestionDraft): CreateTaskPayload => {
    const payload: CreateTaskPayload = { title: draft.title };

    if (draft.doneDate) {
      payload.expected_conclusion_at = toIsoDeadline(
        `${draft.doneDate}T${draft.doneTime || '00:00'}`,
      );
    }
    if (!draft.fatalNotApplicable && draft.fatalDate) {
      payload.deadline = toIsoDeadline(`${draft.fatalDate}T${draft.fatalTime || '00:00'}`);
    }
    if (draft.responsibleId) payload.responsible_id = Number(draft.responsibleId);

    return payload;
  };

  const handleUseSuggestions = (drafts: SuggestionDraft[]) => {
    setSubmitting(true);
    setError(null);

    createBiddingTasksBatch(biddingId, drafts.map(suggestionPayload))
      .then((res) => {
        if (!res || res.status >= 400) {
          setError(
            res?.data?.errors?.[0] ??
              res?.data?.error ??
              'Não foi possível criar as tarefas sugeridas.',
          );
          return;
        }
        onCreatedMany(res.data?.tasks ?? []);
      })
      .finally(() => setSubmitting(false));
  };

  const handleSubmit = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Informe o nome da tarefa.');
      return;
    }

    const payload: CreateTaskPayload = { title: trimmedTitle };
    if (description.trim()) payload.description = description.trim();
    if (deadline) payload.deadline = toIsoDeadline(deadline);
    if (responsibleId) payload.responsible_id = Number(responsibleId);
    if (checklist.length) payload.checklist = checklist;

    setSubmitting(true);
    setError(null);
    createBiddingTask(biddingId, payload)
      .then((res) => {
        const created = res?.data?.task;
        if (!res || res.status >= 400 || !created?.id) {
          setError(
            res?.data?.errors?.[0] ?? res?.data?.error ?? 'Não foi possível criar a tarefa.',
          );
          return undefined;
        }

        return addNotesInOrder(created.id, notes).then((comments) =>
          onCreated({
            ...created,
            counts: { ...created.counts, comments: created.counts.comments + comments.length },
            comments: [...(created.comments ?? []), ...comments],
          }),
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Container className="flex-shrink-0 border-left bg-white d-flex flex-column">
      <div className="task-new-header d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
        <h4 className="mb-0">Adicionar tarefa</h4>
        <button
          type="button"
          className="btn btn-link text-dark p-0 d-flex align-items-center"
          aria-label="Fechar"
          onClick={onClose}
        >
          <i className="material-icons">close</i>
        </button>
      </div>

      {step === 'choice' && (
        <div className="task-choice text-center px-4 pt-5 pb-4">
          <p className="task-choice-text mx-auto mb-3">
            Crie tarefas manualmente ou use nosso analista de tarefas para começar rapidamente.
          </p>

          <div className="d-flex align-items-center justify-content-center flex-wrap">
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center mr-2"
              onClick={() => setStep('form')}
            >
              <i className="material-icons mr-1">add</i>
              Criar tarefa
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-outline-ia mr-2"
              onClick={() => setStep('suggestions')}
              disabled={loadingSuggestions || suggestions.length === 0}
            >
              <span className="span-outline-ia d-flex align-items-center">
                {' '}
                <i className="material-icons mr-1">auto_awesome</i>
                Usar sugestões
                {loadingSuggestions && (
                  <span className="spinner-border spinner-border-sm ml-2" role="status" />
                )}
              </span>
            </button>
          </div>

          {suggestions.length > 0 && (
            <div className="rounded-pill mt-3 px-3 py-1 btn-outline-ia-opacity">
              <span className="d-flex align-items-center justify-content-center span-outline-ia">
                <i className="material-icons-outlined mr-2">smart_toy</i>
                {suggestions.length}{' '}
                {suggestions.length === 1 ? 'tarefa recomendada' : 'tarefas recomendadas'} com base
                no edital
              </span>
            </div>
          )}

          <p className="task-choice-text task-choice-hint text-muted mx-auto mt-3 mb-0">
            O Analista de Tarefas verifica o edital e cria um conjunto de tarefas recomendadas para
            esta licitação com datas sugeridas.
          </p>
        </div>
      )}

      {step === 'suggestions' && (
        <AiManagementTaskSuggestions
          suggestions={suggestions}
          saving={submitting}
          onBack={() => setStep('choice')}
          onConfirm={handleUseSuggestions}
        />
      )}

      {step === 'form' && (
        <>
          <div className="task-new-body px-4 pb-4">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="new-task-title">Nome da tarefa</label>
              <input
                id="new-task-title"
                type="text"
                className="form-control"
                maxLength={TITLE_MAX}
                placeholder="Digite o nome da tarefa"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group col-md-7">
                <label htmlFor="new-task-responsible">Responsável</label>
                <select
                  id="new-task-responsible"
                  className="form-control"
                  value={responsibleId}
                  onChange={(event) => setResponsibleId(event.target.value)}
                >
                  <option value="">Sem responsável</option>
                  {team.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-5">
                <label htmlFor="new-task-deadline">Prazo</label>
                <AiManagementDatetimePicker
                  id="new-task-deadline"
                  value={deadline}
                  onChange={setDeadline}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="new-task-description">Descrição</label>
              <textarea
                id="new-task-description"
                className="form-control"
                rows={4}
                placeholder="Digite uma descrição para a tarefa"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>

            <div className="task-section-title">Checklist</div>
            <AiManagementChecklist
              items={checklist.map((content, index) => ({
                key: String(index),
                content,
                done: false,
              }))}
              onAdd={addChecklistItem}
              onRemove={(key) => removeChecklistItem(Number(key))}
            />

            <div className="task-section-title">Anotações</div>
            {notes.map((note) => (
              <div
                key={note.key}
                className="task-note rounded d-flex justify-content-between align-items-start p-3 mb-2"
              >
                <span className="text-break">{note.text}</span>
                <button
                  type="button"
                  className="task-note-delete btn btn-link p-0 ml-2"
                  onClick={() => removeNote(note.key)}
                >
                  Remover
                </button>
              </div>
            ))}
            <CommentInput isLoading={false} createComment={addNote} commentSource="ai_management" />
          </div>

          <div className="task-new-footer d-flex align-items-center justify-content-end border-top px-4 py-3">
            <button
              type="button"
              className="btn btn-outline-secondary mr-2"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !title.trim()}
            >
              {submitting ? 'Criando...' : 'Criar tarefa'}
            </button>
          </div>
        </>
      )}
    </Container>
  );
};

export default AiManagementTaskNew;
